import { ConnectorGlyph } from '@/components/points/ConnectorGlyph';
import { ChargerStatusBadge, isChargerSelectable } from '@/components/points/ChargerStatusBadge';
import { formatConnectorType } from '@/api/points';
import { chargerPeakKw } from '@/components/stations/ChargerCard';
import { connectorLabel, hasConnectorLetters } from '@/lib/connectorLabel';
import type { StationCharger } from '@/api/stations';

interface Props {
  charger: StationCharger;
  index: number;
  onSelectConnector: (connectorId: string) => void;
}

/** Linha compacta de carregador — badge numerado + kW à esquerda, conectores
 * empilhados à direita. Usado no detalhamento por tipo (feedback Saulo
 * 2026-07-07, mapfirst-multi-noscroll.jsx · G detalhe · `ChargerLine`). */
export function ChargerLineRow({ charger, index, onSelectConnector }: Props) {
  const kw = Math.round(chargerPeakKw(charger));
  const letters = hasConnectorLetters(charger.capabilities);
  // O card INTEIRO navega — mesma semântica do ChargerCard: primeiro conector
  // livre, ou o primeiro se todos ocupados (o ConfigureCharge mostra o estado
  // e é lá que o cliente decide conector/pagamento). Antes só as linhas de
  // conector eram alvo (~28px cada) e a linha ocupada era disabled — com
  // alguém carregando, metade da área clicável morria e o cliente "caçava"
  // onde tocar (docs/bugs/2026-08-17-card-carregador-area-toque.md).
  const fallback =
    charger.connectors.find((c) => isChargerSelectable(c.state)) ??
    charger.connectors[0];
  return (
    <div
      onClick={() => fallback && onSelectConnector(fallback.id)}
      className="flex cursor-pointer items-center gap-3 rounded-2xl border border-bow-royal/8 bg-white p-2.5 shadow-sm"
    >
      <div className="flex w-11 flex-shrink-0 flex-col items-center gap-1">
        <span className="flex h-8 w-8 items-center justify-center rounded-[10px] bg-bow-royal text-[13px] font-bold text-bow-cream">
          {String(index + 1).padStart(2, '0')}
        </span>
        <span className="text-[10px] font-semibold text-bow-royal/55">{kw} kW</span>
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        {charger.connectors.map((c) => {
          const selectable = isChargerSelectable(c.state);
          return (
            <button
              key={c.id}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onSelectConnector(c.id);
              }}
              disabled={!selectable}
              // pointer-events-none no disabled: o toque atravessa pro card
              // (que navega no fallback) em vez de morrer no botão.
              className="flex items-center gap-2 text-left disabled:pointer-events-none disabled:opacity-70"
            >
              <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-bow-cream-2/70 text-bow-royal">
                <ConnectorGlyph type={c.type} size={14} />
              </span>
              <span className="min-w-0 flex-1 truncate text-[12px] font-semibold text-bow-royal">
                {connectorLabel(c.connector_number, letters)} · {formatConnectorType(c.type)}
              </span>
              <ChargerStatusBadge state={c.state} />
            </button>
          );
        })}
      </div>
    </div>
  );
}
